import { NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TranslatorService } from '@sc/data-access-translation';
import { IconComponent } from '@sc/ui-components';
import { dayjs } from '@sc/utils';
import { NotificationInfos } from '../../models';

@Component({
  selector: 'sc-notification-center-body',
  styleUrls: ['./notification-center-body.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, NgFor, IconComponent, TranslateModule],
  template: `
    <div class="container">
      <div class="header">
        <h2>
          {{ 'FeatureFitterNavBar.NotificationCenter.title' | translate }}
          {{ getTitleNbUnseen() }}
        </h2>
        <span
          *ngIf="nbNotifsUnseen() > 0"
          class="mark-as-read-btn"
          (click)="onMarkAsReadClick()"
          >{{
            'FeatureFitterNavBar.NotificationCenter.markAsReadBtn' | translate
          }}</span
        >
      </div>
      <div class="notifications-list">
        <div
          *ngFor="let notification of notifications"
          class="notif-item"
          (mouseover)="notification.seen = true"
        >
          <div class="left-indicators">
            <span
              class="seen-indicator"
              [class.is-seen]="notification.seen"
            ></span>
          </div>
          <div class="notif-body">
            <span class="notif-title">{{
              notification.title | translate
            }}</span>
            <span class="notif-apmt-infos"
              ><span>{{
                notification.fitterName ||
                  ('FeatureFitterNavBar.NotificationCenter.unknwonFitterName'
                    | translate)
              }}</span
              ><span *ngIf="notifHasTime(notification)">
                -
                {{ getLocaleDateTime(notification) }}</span
              ></span
            >
            <span class="notif-customer-infos"
              >{{ getNotifVehicleDescription(notification) }} -
              {{ notification.customerFirstName }}
              {{ notification.customerLastName }}</span
            >
            <span *ngIf="notification.alert" class="notif-alert-infos">
              <sc-icon name="warning_circle"></sc-icon>
              <span>{{ notification.alert | translate }}</span>
            </span>
          </div>
        </div>
        <div *ngIf="!notifications.length" class="notif-empty-bloc">
          {{
            'FeatureFitterNavBar.NotificationCenter.noNotifications' | translate
          }}
        </div>
      </div>
      <div class="actions">
        <div
          *ngIf="!isFullNotifList"
          class="btn-load-more"
          (click)="onChargeMoreNotifsClick()"
        >
          <span>{{
            'FeatureFitterNavBar.NotificationCenter.loadMoreNotifsBtn'
              | translate
          }}</span>
        </div>
      </div>
    </div>
  `,
})
export class NotificationCenterBodyComponent {
  @Input() notifications: NotificationInfos[] = [];
  @Input() isFullNotifList = true;

  @Output() markAsRead = new EventEmitter<void>();
  @Output() chargeMoreNotifs = new EventEmitter<void>();

  constructor(private readonly translator: TranslatorService) {}

  nbNotifsUnseen() {
    return this.notifications.filter((notif) => !notif.seen).length;
  }

  getTitleNbUnseen() {
    const nbNotifs = this.nbNotifsUnseen();
    return nbNotifs > 0 ? `(${nbNotifs})` : '';
  }

  notifHasTime(notif: NotificationInfos) {
    return !!notif.timestamp && !!notif.timezone;
  }

  getNotifVehicleDescription(notif: NotificationInfos) {
    return (
      [notif.carBrand, notif.carMordel].filter((text) => !!text).join(' ') ||
      this.translator.getInstantTranslation(
        'FeatureFitterNavBar.NotificationCenter.unknownVehicle'
      )
    );
  }

  onMarkAsReadClick() {
    this.markAsRead.emit();
  }

  onChargeMoreNotifsClick() {
    this.chargeMoreNotifs.emit();
  }

  getLocaleDateTime(notification: NotificationInfos): string {
    return dayjs(notification.timestamp)
      .tz(notification.timezone)
      .format('LLLL');
  }
}
